import { Button, ButtonSizeType } from '@design-system';
import { isPast } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect } from 'react';

import { skuRef } from '../../../helpers/purchasing';
import { useIsSubscribed } from '../../../hooks/subscription';
import { useAuthenticate } from '../../../hooks/useAuthenticate';
import { useAuth } from '../../../providers';
import { useDraws } from '../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../providers/PreAuthDrawProvider';
import { useStore } from '../../../store/utils';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { buildSubscriptions } from '../../../utils/subscriptions';
import { AuthEntryPoint } from '../../auth/types';
import { useDrawButtonLabels } from '../../draws/hooks/useDrawButtonLabels';
import { DrawWithFrames } from '../../draws/types';

type Props = {
  buttonLocation: 'dialogCta' | 'mainCta' | 'stickyCta';
  auctionWithFrames: DrawWithFrames;
  hasCheckoutParam?: boolean;
  startAuctionFlow: () => void;
  size?: ButtonSizeType;
  fullWidth?: boolean;
  updateBid?: boolean;
};

export const PreAuthAuctionButton = ({
  auctionWithFrames,
  buttonLocation,
  fullWidth = true,
  hasCheckoutParam = false,
  size = 'lg',
  startAuctionFlow,
  updateBid = false,
}: Props) => {
  const { draw: auction, frames, product } = auctionWithFrames;
  const { isLoggedIn } = useAuth();
  const [emitter] = useAnalyticsEmitter();
  const { t } = useTranslation(['product', 'authentication']);
  const { authenticateAndSubscribe } = useAuthenticate();
  const auctionEntry = useStore((s) => s.features.draw.drawEntry[auction.id]);
  const {
    activeDraw,
    customerSelectedFrame,
    loading,
    setupComplete,
    setupController,
  } = usePreAuthDrawCtx();
  const { isAccountInDraw } = useDraws();

  const labels = useDrawButtonLabels(buttonLocation, false, false, true);

  const drawVariant = product.variants.find((v) => v.title === 'Draw');

  const subscriptions = buildSubscriptions({
    'artists': product.artists.map((a) => ({
      name: a.name,
      internalId: a.id,
    })),
    'isPreOrder': false,
    'productId': auction.productId,
    'productRef': skuRef(drawVariant?.sku ?? ''),
    'productTitle': product.title,
  });

  const { isSubscribedAll, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  const start = useCallback(async () => {
    if (!setupComplete || activeDraw?.draw.id !== auction.id) {
      await setupController(auctionWithFrames);
    }
    startAuctionFlow();
  }, [
    activeDraw?.draw.id,
    auction.id,
    auctionWithFrames,
    setupComplete,
    setupController,
    startAuctionFlow,
  ]);

  const enterAuction = useCallback(() => {
    if (isLoggedIn && isSubscribedAll) {
      start();
      return;
    }
    authenticateAndSubscribe({
      authScreenThumbnail: product.thumbnail?.url,
      authScreenTitle: t('authentication:dynamicTitle.auction'),
      analyticsProperties: {
        'artist': product.artists[0].name,
        'artist name': product.artists[0].name,
        'button_location': 'product page',
        'product_id': auction.productId,
        'product_status': 'coming soon',
        'product_title': product.title,
        'source': AuthEntryPoint.AuctionEntry,
      },
      hideSuccessScreen: true,
      onFlowComplete: () => {
        setTimeout(() => {
          startAuctionFlow();
          refetch();
        }, 150);
      },
      optInGeneral: false,
      subscriptions,
    });

    if (isLoggedIn) {
      startAuctionFlow();
    }
  }, [
    isLoggedIn,
    isSubscribedAll,
    authenticateAndSubscribe,
    product.thumbnail?.url,
    product.artists,
    product.title,
    t,
    auction.productId,
    subscriptions,
    start,
    startAuctionFlow,
    refetch,
  ]);

  useEffect(() => {
    if (hasCheckoutParam && buttonLocation === 'mainCta') {
      enterAuction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonLocation, hasCheckoutParam]);

  if (
    isLoggedIn &&
    (auctionEntry?.entered || isAccountInDraw(auction.id)) &&
    !updateBid
  ) {
    return (
      <Button
        disabled
        fullWidth={fullWidth}
        icon="ic_check"
        label={t(labels.success)}
        size="lg"
        variant="positive"
      />
    );
  }

  return (
    <Button
      disabled={isPast(new Date(auction.closeAt))}
      fullWidth={fullWidth}
      label={t(updateBid ? 'subscriptionCta.auction.updateBid' : labels.normal)}
      loading={loading(auction.id)}
      onClick={() => {
        emitter('draw entry', 'clicked', {
          'type': 'Auction',
          'product title': product.title,
          'artist name': product.artists[0].name,
          'product price': product.price,
          'product can be framed': Boolean(frames?.length),
          'product frame upgrade available': Boolean(frames),
          'product frame chosen': Boolean(customerSelectedFrame),
        });
        enterAuction();
      }}
      size={size}
      variant={updateBid ? 'tertiary' : 'primary'}
    />
  );
};
